import { ApiError } from '@/api/client'
import { AuthSessionExpiredError } from '@/api/authSession'
import { isTechnicalErrorMessage } from '@/shared/lib/friendly-error'

type TranslateFn = (key: string, opts?: { defaultValue?: string }) => string

export function isCanvasGraphConflict(err: unknown): boolean {
  return err instanceof ApiError && err.code === 40983
}

/** 将 resolveApiErrorMessage 结果转为可展示文案：i18n key 走 t()，已是中文/原文则直出。 */
export function formatResolvedApiError(t: TranslateFn, resolved: string): string {
  if (/^[a-z][\w.]*$/.test(resolved) && resolved.includes('.')) {
    return t(resolved, { defaultValue: resolved })
  }
  return resolved
}

/** 将 AI 任务 error_message 转为可展示的 i18n key 或简短原文。 */
export function simplifyAITaskErrorMessage(raw: string | undefined, fallback: string): string {
  if (!raw?.trim()) return fallback
  let msg = raw.trim().replace(/^ai upstream failed:\s*/i, '')
  if (/copyright restrictions|版权限制|内容安全|content.?policy|moderation/i.test(msg)) {
    return 'canvas.errors.videoCopyrightBlocked'
  }
  if (/read_response_body_failed|goaway/i.test(msg)) {
    return 'canvas.errors.providerRelayFailed'
  }
  if (/speaker_map|speaker_audio|mega_info/i.test(msg)) {
    return 'canvas.errors.seedAudioVoiceMismatch'
  }
  if (/system_memory_overloaded|system memory overloaded/i.test(msg)) {
    return 'canvas.errors.providerMemoryOverload'
  }
  if (/system_cpu_overloaded|system cpu overloaded/i.test(msg)) {
    return 'canvas.errors.providerCpuOverload'
  }
  if (/http 503|service unavailable|provider unavailable/i.test(msg)) {
    return 'canvas.errors.providerUnavailable'
  }
  const jsonStart = msg.indexOf('{')
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(msg.slice(jsonStart)) as { error?: { message?: string }; message?: string }
      const inner = parsed.error?.message ?? parsed.message
      if (typeof inner === 'string' && inner.trim()) {
        if (/system memory overloaded/i.test(inner)) return 'canvas.errors.providerMemoryOverload'
        if (/system cpu overloaded/i.test(inner)) return 'canvas.errors.providerCpuOverload'
        if (!isTechnicalErrorMessage(inner)) return inner.trim()
      }
    } catch {
      /* ignore malformed JSON tail */
    }
  }
  if (isTechnicalErrorMessage(msg)) return fallback
  return msg
}

/** Map backend business codes to i18n keys; fall back to caller-provided key. */
export function resolveApiErrorMessage(err: unknown, fallback: string, opts?: { taskType?: string }): string {
  if (err instanceof AuthSessionExpiredError) {
    return 'sessionExpired'
  }
  if (err instanceof ApiError) {
    if (err.isAuthError()) return 'sessionExpired'
    if (err.code === 40031 && err.message.trim()) return err.message.trim()
    if (err.code === 50101) return 'canvas.errors.streamNotSupported'
    if (err.code === 50102) return 'account.security.realnameNationalNidUnavailable'
    if (err.code === 40496) return 'featureDisabled'
    if (err.code === 40089) return 'storage.custom.notConfiguredError'
    if (err.code === 40983) return 'canvas.errors.conflict'
    if (err.code === 40971) return 'tasks.assignmentApproveBlocked'
    if (err.code === 40231) return 'team.collabRequired'
    if (err.code === 40232) return 'team.seatsExceeded'
    if (err.code === 40371) return 'team.ownerOnly'
    if (err.code === 40387) return 'team.ownerOnly'
    if (err.code === 40392 && err.message.toLowerCase().includes('writer import')) return 'writer.import.forbidden'
    if (err.code === 40484) return 'writer.import.sourceNotFound'
    if (err.code === 40086) return 'script.aiBatchNoTargets'
    if (err.code === 40099) return 'writer.import.partsRequired'
    if (err.code === 40096) return 'writer.editor.agentNeedSeriesOrStory'
    if (err.code === 40097) return 'writer.editor.agentNeedEpisodeOutline'
    if (err.code === 40083) return 'script.aiBatchScopeRequired'
    if (err.code === 40020) return 'script.aiInsufficientPoints'
    if (err.code === 40040) return 'contests.errors.contestClosed'
    if (err.code === 40041) return 'contests.errors.votingClosed'
    if (err.code === 40042) return 'contests.errors.judgingClosed'
    if (err.code === 40043) return 'contests.errors.entryNotApproved'
    if (err.code === 40044) return 'contests.errors.workNotPublished'
    if (err.code === 40220) return 'contests.errors.insufficientPoints'
    if (err.code === 40222) return 'budget.insufficientForAI'
    if (err.code === 40340) return 'contests.errors.workNotOwned'
    if (err.code === 40941) return 'contests.errors.alreadySubmitted'
    if (err.code === 40942) return 'contests.errors.alreadyVoted'
    if (err.code === 40944) return 'contests.errors.voteIpBlocked'
    if (err.code === 40442) return 'contests.errors.workNotFound'
    if (err.code === 40470) {
      const msg = err.message.toLowerCase()
      if (msg.includes('member')) return 'team.memberNotFound'
      if (msg.includes('department')) return 'team.deptNotFound'
      if (msg.includes('invite')) return 'invite.notFound'
      if (msg.includes('script')) return 'writer.import.scriptNotFound'
      return fallback
    }
    // 5xx / 网关英文技术文案（如 Service Unavailable）→ 调用方友好 fallback
    if (err.status >= 500 || isTechnicalErrorMessage(err.message)) {
      return fallback
    }
    if (err.message.trim()) return err.message.trim()
  }
  if (err instanceof Error && err.message.toLowerCase().includes('access token expired')) {
    return 'sessionExpired'
  }
  if (err instanceof Error && err.name === 'AITaskPollTimeoutError') {
    return opts?.taskType === 'audio' ? 'canvas.errors.pollTimeoutAudio' : 'canvas.errors.pollTimeout'
  }
  if (err instanceof Error && err.message.includes('AI task poll timeout')) {
    return opts?.taskType === 'audio' ? 'canvas.errors.pollTimeoutAudio' : 'canvas.errors.pollTimeout'
  }
  if (err instanceof DOMException && err.name === 'AbortError') {
    return opts?.taskType ? 'canvas.errors.pollTimeout' : 'canvas.errors.requestTimeout'
  }
  if (err instanceof Error && /aborted a request|signal timed out|AbortError/i.test(err.message)) {
    return opts?.taskType ? 'canvas.errors.pollTimeout' : 'canvas.errors.requestTimeout'
  }
  if (err instanceof Error && isTechnicalErrorMessage(err.message)) {
    return fallback
  }
  if (err instanceof Error && err.message.trim()) {
    return err.message.trim()
  }
  return fallback
}
